import { BugReportRounded, Facebook, Instagram, LinkedIn } from '@mui/icons-material'
import { Box, Divider, Grid, Tooltip, Typography, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router'
import { IRootState } from '../../../../redux/types'
import useScreenWidth from '../../hooks/useScreenWidth'

export const FooterText = styled(Typography)(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  '& a': {
    color: theme.palette.secondary.main,
    textDecorationColor: 'transparent',
    display: 'flex',
    '& svg': {
      color: theme.palette.grey[400]
    }
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: 16,
    '& a': {
      '& svg': {
        fontSize: 28
      }
    }
  }
}))

export const FooterTextLR = styled(Typography)(({ theme }) => ({
  justifyContent: 'center',
  display: 'flex',
  alignItems: 'flex-end',
  gap: '8px',
  paddingTop: 8,
  flexWrap: 'wrap',
  textAlign: 'center',
  '& a': {
    color: theme.palette.secondary.main,
    textDecorationColor: 'transparent',
    display: 'flex',
    '& svg': {
      color: theme.palette.grey[400]
    }
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: 16,
    '& a': {
      '& svg': {
        fontSize: 28
      }
    }
  }
}))

export const BoxTextLR = styled(Box)(({ theme }) => ({
  justifyContent: 'center',
  display: 'flex',
  alignItems: 'flex-end',
  gap: '8px',
  paddingTop: 8,
  flexWrap: 'wrap',
  textAlign: 'center',
  '& a': {
    color: theme.palette.secondary.main,
    textDecorationColor: 'transparent',
    display: 'flex',
    '& svg': {
      color: theme.palette.grey[400]
    }
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: 16,
    '& a': {
      '& svg': {
        fontSize: 28
      }
    }
  }
}))

export const FooterBox = styled(Box)(({ theme }) => ({
  '&.dark': {
    backgroundColor: theme.palette.grey[800],
    '& span': {
      color: `${theme.palette.grey[200]} !important`
    },
    '& a': {
      '& span': {
        color: `${theme.palette.primary.main} !important`
      },
      '& svg': {
        color: `${theme.palette.common.white} !important`
      }
    }
  },
  '& a': {
    maxHeight: 22,
    textDecorationColor: 'transparent'
  }
}))

type FooterProps = {
  dark?: boolean
  noMargin?: boolean
}

const Footer = ({ dark = false, noMargin = false }: FooterProps) => {
  const { t } = useTranslation(['global'])
  const isTablet = useScreenWidth(960)
  const location = useLocation()

  const { user: authUser } = useSelector((state: IRootState) => state.auth)
  const isAdmin = location.pathname.includes('admin') || location.pathname === '/escritorio' || location.pathname === '/usuarios/perfil'
  const isInHomePage = location.pathname === '/inicio'
  const shouldShowMobileMenu = isAdmin || isInHomePage || isTablet

  return (
    <>
      {authUser !== null ? (
        <Box
          sx={theme => ({
            marginBottom: '-100px',
            [theme.breakpoints.down('sm')]: {
              marginBottom: '100px'
            }
          })}
          mt={6}
          bottom="0"
          top="100%"
          ml={shouldShowMobileMenu || noMargin ? '0px' : '235px'}
          className={dark ? 'dark' : ''}
        >
          <Divider style={{ backgroundColor: '#C7CCD1' }} />
          <Box
            sx={theme => ({
              [theme.breakpoints.down('sm')]: {
                flexFlow: 'wrap-reverse',
                '& hr': {
                  display: 'none'
                }
              }
            })}
            display="flex"
            flexWrap="wrap"
            justifyContent="center"
          >
            <Box display="flex" gap={1} alignItems="center">
              <Divider style={{ backgroundColor: '#C7CCD1' }} orientation="vertical" flexItem />
              <Tooltip placement="top-start" title={`${t('report-a-problem')}`}>
                <FooterText align="center" variant="caption" color="textSecondary">
                  <a rel="noopener noreferrer" target="_blank" href="https://drv.es/reportar-error-en-plei/">
                    <BugReportRounded fontSize="small" />
                  </a>
                </FooterText>
              </Tooltip>
              <Divider style={{ backgroundColor: '#C7CCD1' }} orientation="vertical" flexItem />
            </Box>
            <Box
              alignContent="center"
              gap={2}
              justifyContent="center"
              display="flex"
              flexWrap="wrap"
              p={1}
              alignItems="center"
              margin="0 auto"
            >
              <FooterText align="center" variant="caption" color="textSecondary">
                {t('footer.shortfooter', {
                  year: `${new Date().getFullYear()}`,
                  COMPANY_NAME: import.meta.env.VITE_COMPANY_NAME
                })}
              </FooterText>
              {location.pathname.includes('ayuda') ? (
                <FooterText align="center" variant="caption" color="textSecondary">
                  |
                </FooterText>
              ) : (
                <FooterText align="center" variant="caption" color="textSecondary">
                  |
                  <Link to="/ayuda">
                    <Typography variant="caption" style={{ color: '##FC3E6F' }}>
                      {t('help')}
                    </Typography>
                  </Link>
                  |
                </FooterText>
              )}
              <FooterText align="center" variant="caption" color="textSecondary">
                <a href="https://www.facebook.com/GrupoDRV/">
                  <Facebook fontSize="small" />
                </a>
                <a href="https://www.linkedin.com/company/grupo-drv-phytolab/">
                  <LinkedIn fontSize="small" />
                </a>
              </FooterText>
            </Box>
          </Box>
        </Box>
      ) : (
        <Grid item xs={12}>
          <FooterBox p={3} px={4} maxWidth="100%" gap={2} className={dark ? 'dark' : ''}>
            {location.pathname !== '/registro' ? (
              location.pathname === '/contrasena-cliente' ? (
                ''
              ) : (
                <BoxTextLR>
                  <Typography variant="caption" color="textSecondary">
                    {t('footer.signupcopy')}
                  </Typography>
                  <Link to="/registro">
                    <Typography variant="caption" color="primary">
                      {t('footer.signup')}
                    </Typography>
                  </Link>
                </BoxTextLR>
              )
            ) : (
              <BoxTextLR>
                <Typography variant="caption" color="textSecondary">
                  {t('login.logincopy')}
                </Typography>
                <Link to="/iniciar-sesion">
                  <Typography variant="caption" color="primary">
                    {t('login.login')}
                  </Typography>
                </Link>
              </BoxTextLR>
            )}
            <Box>
              <FooterTextLR align="center" variant="caption" color="textSecondary">
                {t('footer.shortfooter', {
                  year: `${new Date().getFullYear()}`,
                  COMPANY_NAME: import.meta.env.VITE_COMPANY_NAME
                })}
                <a href={import.meta.env.VITE_RRSS_FACEBOOK_URL}>
                  <Facebook fontSize="small" />
                </a>
                <a href={import.meta.env.VITE_RRSS_INSTAGRAM_URL}>
                  <Instagram fontSize="small" />
                </a>
              </FooterTextLR>
            </Box>
            <BoxTextLR>
              <Tooltip title={`${t('report-a-problem')}`}>
                <FooterTextLR align="center" variant="caption" color="textSecondary">
                  <a rel="noopener noreferrer" target="_blank" href="https://drv.es/reportar-error-en-plei/">
                    <BugReportRounded fontSize="small" />
                  </a>
                </FooterTextLR>
              </Tooltip>
            </BoxTextLR>
          </FooterBox>
        </Grid>
      )}
    </>
  )
}

export default Footer
